import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, CircleAlert, ExternalLink, Link2, LoaderCircle, RefreshCw, ShieldCheck, X } from "lucide-react";
import { apiFetch } from "@/lib/api";

/** User-owned OAuth connections only. No passwords or third-party logins in EARNOS chat. */
export default function ChatConnections({ onClose, missionId = null, initialService = "", initialGoal = "" }) {
  const nav = useNavigate();
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [handoffReady, setHandoffReady] = useState(null);
  const [website, setWebsite] = useState("");
  const [browserCost, setBrowserCost] = useState(false);
  const [session, setSession] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [allowedHosts, setAllowedHosts] = useState([]);
  const [service, setService] = useState(initialService);
  const [serviceGoal, setServiceGoal] = useState(initialGoal.slice(0,500));
  const [requestedAccess, setRequestedAccess] = useState("draft");
  const [requestFeedback, setRequestFeedback] = useState(null);
  let siteHost = "";
  try {
    const site = new URL(website.trim());
    if (site.protocol === "https:" && !site.username && !site.password) siteHost = site.hostname.toLowerCase();
  } catch { /* Not a valid public site yet. */ }
  const siteAllowed = Boolean(siteHost && allowedHosts.some(x => siteHost === x || siteHost.endsWith("." + x)));
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/connections");
      setProviders(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) { setError(e.message || "Connected apps unavailable"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    // A customer can return from provider OAuth to the original chat tab.
    // Refresh from EARNOS server; a browser return alone is not connection proof.
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);
  useEffect(() => {
    apiFetch("/browser/domains").then(rows => {
      if (Array.isArray(rows)) setAllowedHosts(rows.map(x => x.domain));
    }).catch(() => { /* Site authorization stays false until a confirmed read. */ });
  }, []);
  useEffect(() => {
    let active = true;
    apiFetch("/browser-host/readiness").then(result => {
      if (active) setHandoffReady(result);
    }).catch(() => {
      if (active) setHandoffReady({handoff_configured: false});
    });
    return () => { active = false; };
  }, []);

  async function startBrowser(e) {
    e.preventDefault();
    if (busy || !browserCost || !handoffReady?.handoff_configured ||
        !siteAllowed || !website.trim().startsWith("https://")) return;
    setBusy("browser"); setError("");
    try {
      const data = await apiFetch("/browser-host/login-session", {
        method: "POST", body: {
          website_url: website.trim(),
          authorize_cloud_browser_cost: true,
        },
      });
      const live = new URL(data.live_url);
      if (!data.session_id || live.protocol !== "https:" || live.username || live.password) {
        throw new Error("Provider did not return a secure hosted sign-in");
      }
      setSession(data);
      setInspection(null);
    } catch (e) { setError(e.message || "Secure sign-in could not start"); }
    finally { setBusy(""); }
  }

  async function finishBrowser() {
    if (busy || !session) return;
    setBusy("browser-finish"); setError("");
    try {
      const data = await apiFetch(
        "/browser-host/login-session/" + encodeURIComponent(session.session_id) + "/finished",
        {method: "POST"});
      setSession(row => ({...row, customer_reported_login: data.customer_reported_login}));
    } catch (e) { setError(e.message || "Could not record sign-in"); }
    finally { setBusy(""); }
  }

  async function inspectBrowser() {
    if (busy || !session) return;
    setBusy("browser-inspect"); setError("");
    try {
      const data = await apiFetch(
        "/browser-host/login-session/" + encodeURIComponent(session.session_id) + "/inspection");
      setInspection(data);
    } catch (e) { setError(e.message || "Browser inspection unavailable"); }
    finally { setBusy(""); }
  }

  async function attachBrowserToMission() {
    if (busy || !missionId || !session?.customer_reported_login) return;
    setBusy("browser-attach"); setError("");
    try {
      await apiFetch("/mission/missions/" + encodeURIComponent(missionId) + "/browser-login", {
        method: "POST", body: {session_id: session.session_id},
      });
      onClose();
    } catch (e) { setError(e.message || "Unable to attach this login to the mission"); }
    finally { setBusy(""); }
  }

  async function stopBrowser() {
    if (busy || !session) return;
    setBusy("browser-stop"); setError("");
    try {
      await apiFetch("/browser-host/login-session/" +
        encodeURIComponent(session.session_id) + "/stop", {method: "POST"});
      setSession(null); setInspection(null); setBrowserCost(false);
    } catch (e) { setError(e.message || "Cannot confirm browser termination"); }
    finally { setBusy(""); }
  }

  async function allowWebsite() {
    if (!siteHost || busy) return;
    setBusy("allow-site"); setError("");
    try {
      await apiFetch("/browser/domains", {
        method: "POST", body: {domain: siteHost},
      });
      const rows = await apiFetch("/browser/domains");
      setAllowedHosts(Array.isArray(rows) ? rows.map(x => x.domain) : []);
    } catch (e) { setError(e.message || "Website permission could not be saved"); }
    finally { setBusy(""); }
  }

  async function requestApp(e) {
    e.preventDefault();
    if (busy || service.trim().length < 2 || serviceGoal.trim().length < 8) return;
    setBusy("request-app"); setError(""); setRequestFeedback(null);
    try {
      const result = await apiFetch("/connections/requests", {
        method: "POST",
        body: {service: service.trim(), goal: serviceGoal.trim(),
               desired_access: requestedAccess},
      });
      setRequestFeedback(result.capability || {state: result.state});
    } catch (e) { setError(e.message || "This app request could not be saved"); }
    finally { setBusy(""); }
  }

  const signIn = async provider => {
    if (busy || !provider.connection_required || !provider.oauth_configured) return;
    // Open during the actual tap so iOS browsers don't block the authorized
    // provider tab after an asynchronous API request. Keep the EARNOS chat.
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      try { popup.opener = null; } catch { /* Browser owns isolation. */ }
    }
    setBusy(provider.kind); setError("");
    try {
      const result = await apiFetch("/connections/" + encodeURIComponent(provider.kind) + "/start",
        {method:"POST", body: {private: false}});
      const u = new URL(result.authorize_url);
      if (u.protocol !== "https:" || !u.hostname || u.username || u.password)
        throw new Error("A secure provider sign-in was not returned");
      if (popup && !popup.closed) popup.location.assign(u.href);
      else window.location.assign(u.href);
      setBusy("");
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      setError(e.message || "Sign-in unavailable"); setBusy("");
    }
  };
  return <div role="dialog" aria-modal="true" aria-label="Connect your approved apps"
    className="fixed z-50 inset-0 bg-black/75 flex justify-center items-end sm:items-center p-0 sm:p-4">
    <div className="w-full max-w-lg max-h-[88dvh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-surface-2 border border-line shadow-2xl overflow-hidden">
      <header className="px-5 py-4 flex gap-3 items-center justify-between border-b border-line">
        <div className="min-w-0"><h2 className="font-extrabold text-ink flex items-center gap-2"><Link2 size={20}/> Connect your apps</h2>
          <p className="text-xs text-ink-2 mt-1">Choose an account. EARNOS asks before making external changes.</p>
        </div>
        <button type="button" aria-label="Close connections" onClick={onClose}
          className="p-2 rounded-lg border border-line text-ink-2"><X size={20}/></button>
      </header>
      <div className="overflow-auto p-4 space-y-3">
        {loading ? <p className="flex items-center text-sm text-ink-2 gap-2"><LoaderCircle size={17} className="animate-spin"/> Checking supported connections…</p> : null}
        {error ? <p role="alert" className="text-sm text-danger flex gap-2"><CircleAlert size={17}/>{error}</p> : null}
        {initialService ? <div role="status" className="rounded-xl border border-brand/30 bg-brand/10 p-3 text-sm text-ink"><strong>{initialService}</strong><p className="mt-1 text-xs text-ink-2">Choose an available provider sign-in below, or check this service in the prefilled connection request. Access is confirmed by the server after sign-in.</p></div> : null}
        {[...providers].sort((a,b) => Number((b.name || "").toLowerCase().includes(initialService.toLowerCase())) - Number((a.name || "").toLowerCase().includes(initialService.toLowerCase()))).map(p => <div key={p.kind} className="border border-line rounded-2xl p-3 flex gap-3 items-start justify-between">
          <div className="min-w-0">
            <p className="font-bold text-ink text-sm">{p.name}</p>
            <p className="text-xs text-ink-2 mt-1">{p.connected ?
              "Connected to " + (p.account || "your account") :
              (p.platform_status || "Not configured")}</p>
            <p className="text-xs text-ink-2 mt-1">{(p.read_actions || []).slice(0,2).join(" · ")}</p>
            {p.actions_note ? <p className="text-xs text-amber-300 mt-1">{p.actions_note}</p> : null}
          </div>
          {p.connected ? <span className="flex gap-1 items-center text-xs text-ok font-semibold"><CheckCircle2 size={15}/> Connected</span> :
            p.connection_required && p.oauth_configured ?
            <button type="button" disabled={Boolean(busy)} onClick={() => signIn(p)}
              className="shrink-0 rounded-xl bg-brand text-white px-3 py-2 text-xs font-bold disabled:opacity-50">
              {busy === p.kind ? "Opening…" : "Sign in"} <ExternalLink size={13} className="inline"/></button> :
            <span className="text-xs text-ink-2 shrink-0">Not live</span>}
        </div>)}
        {!loading && providers.length === 0 && !error ?
          <p className="text-sm text-ink-2">No service connections have been configured on this server.</p> : null}
        <section className="rounded-2xl border border-brand/40 bg-brand/5 p-4 space-y-3"
          aria-label="Secure account sign-in" data-testid="earnos-inline-cloud-signin">
          <p className="font-bold text-ink">Sign in to a website for EARNOS work</p>
          <p className="text-xs text-ink-2">When the hosted browser is enabled, EARNOS opens a short-lived private browser for your selected website. You enter your own login and verification codes there, never in chat. Signing in alone does not approve posts, purchases, or other changes.</p>
          {!handoffReady?.handoff_configured ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-300">Secure hosted sign-in is not configured on this EARNOS server yet. Account actions cannot run from an unsupported login.</p>
              <button type="button" onClick={() => { onClose(); nav("/browser-access" + (missionId ? "?mission=" + encodeURIComponent(missionId) : "")); }}
                className="text-sm text-brand-light font-bold">View browser readiness <ArrowRight size={14} className="inline"/></button>
            </div>
          ) : session ? (
            <div className="space-y-3">
              <p className="text-xs text-ink-2">Private browser expires {new Date(session.expires_at).toLocaleString()}.</p>
              <a href={session.live_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-3 bg-brand rounded-xl font-bold text-sm text-white">
                Open my secure sign-in <ExternalLink size={16}/>
              </a>
              <div className="flex gap-2 flex-wrap">
                <button type="button" disabled={Boolean(busy) || session.customer_reported_login}
                  onClick={finishBrowser}
                  className="rounded-xl border border-line px-3 py-2 text-xs font-bold text-ink disabled:opacity-50">
                  {session.customer_reported_login ? "Sign-in reported" : "I finished signing in"}
                </button>
                <button type="button" disabled={Boolean(busy)} onClick={inspectBrowser}
                  className="rounded-xl border border-line px-3 py-2 text-xs font-bold text-ink">Inspect</button>
                <button type="button" disabled={Boolean(busy)} onClick={stopBrowser}
                  className="rounded-xl border border-line px-3 py-2 text-xs font-bold text-ink">End browser session</button>
              </div>
              {inspection ? <p className="text-xs text-ink-2 break-words">
                Current page: {inspection.page_title || inspection.url}.
                A page readback does not independently prove account authentication.</p> : null}
              {missionId && session.customer_reported_login ? (
                <button type="button" disabled={Boolean(busy)} onClick={attachBrowserToMission}
                  className="rounded-xl bg-brand text-white px-4 py-3 text-sm font-bold disabled:opacity-50">
                  Use this login for my mission <ArrowRight size={16} className="inline"/>
                </button>
              ) : null}
              {!missionId ? <p className="text-xs text-ink-2">
                Start or open a mission from EARNOS chat, then connect its sign-in to that specific job.</p> : null}
            </div>
          ) : (
            <div className="space-y-3">
              <form onSubmit={startBrowser} className="space-y-3">
              <label htmlFor="earnos-inline-website" className="block text-xs font-bold text-ink">
                Website where you want to sign in</label>
              <input id="earnos-inline-website" type="url" maxLength={1500}
                required value={website} onChange={e => setWebsite(e.target.value)}
                placeholder="https://your-approved-service.com"
                className="w-full bg-surface-3 border border-line rounded-xl p-3 text-sm text-ink"/>
              {siteHost && !siteAllowed ? (
                <button type="button" onClick={allowWebsite} disabled={Boolean(busy)}
                  className="w-full rounded-xl border border-brand/40 p-3 text-sm font-bold text-brand-light disabled:opacity-40">
                  Allow {siteHost} for my EARNOS browser
                </button>
              ) : siteAllowed ? (
                <p role="status" className="flex gap-1 items-center text-xs text-ok">
                  <CheckCircle2 size={15}/> Website approved by you: {siteHost}
                </p>
              ) : null}
              <label className="flex gap-2 text-xs text-ink-2">
                <input type="checkbox" checked={browserCost}
                  onChange={e => setBrowserCost(e.target.checked)}/>
                I authorize one short-lived hosted session, which may use EARNOS provider credits.
              </label>
              <button type="submit" disabled={Boolean(busy) || !browserCost || !siteAllowed || !website.startsWith("https://")}
                className="rounded-xl bg-brand text-white px-4 py-3 text-sm font-bold disabled:opacity-50">
                Open private sign-in
              </button>
              <p className="text-xs text-ink-2">Only websites you approve can open. You enter passwords directly in the private browser, not in EARNOS chat.</p>
              </form>
            </div>
          )}
        </section>
        <section className="rounded-xl border border-brand/30 bg-brand/10 p-3 space-y-3"
          aria-label="Request another connected app" data-testid="earnos-inchat-request-app">
          <p className="text-sm font-semibold text-ink">Connect another app</p>
          <p className="text-xs text-ink-2">Name any service you use and what you want done. EARNOS checks for an available integration or secure browser handoff; a request alone does not grant access.</p>
          <form onSubmit={requestApp} className="space-y-2">
            <input type="text" maxLength={80} aria-label="App or service name"
              value={service} onChange={e=>setService(e.target.value)}
              placeholder="e.g. Framer, Instagram, Etsy" required
              className="w-full rounded-xl border border-line bg-surface-3 px-3 py-2 text-sm text-ink"/>
            <textarea rows={2} maxLength={500} aria-label="What EARNOS should do"
              value={serviceGoal} onChange={e=>setServiceGoal(e.target.value)}
              placeholder="What job should EARNOS complete in that account?" required
              className="w-full rounded-xl border border-line bg-surface-3 p-3 text-sm text-ink"/>
            <label className="block text-xs text-ink-2">Permission requested</label>
            <select aria-label="Requested account access" value={requestedAccess}
              onChange={e=>setRequestedAccess(e.target.value)}
              className="w-full rounded-xl border border-line bg-surface-3 px-3 py-2 text-sm text-ink">
              <option value="read">Read only</option>
              <option value="draft">Prepare drafts</option>
              <option value="write">Create or change content, with approval</option>
            </select>
            <button type="submit" disabled={Boolean(busy) || service.trim().length < 2 || serviceGoal.trim().length < 8}
              className="rounded-xl bg-brand px-4 py-2 text-white text-sm font-bold disabled:opacity-40">
              Check connection options
            </button>
          </form>
          {requestFeedback ? <div role="status" className="rounded-lg border border-line bg-surface-2 p-3 text-xs text-ink">
            <p className="font-bold">Request saved · {(requestFeedback.state || "pending").replaceAll("_", " ")}</p>
            <p className="mt-1 text-ink-2">{requestFeedback.note || "No account permissions were granted."}</p>
            {requestFeedback.channel === "provider_api" && requestFeedback.state === "available_to_connect" ?
              <button type="button" onClick={refresh} className="mt-2 text-brand-light font-bold">Show sign-in options</button> : null}
            {requestFeedback.channel === "browser" ?
              <p className="mt-2 text-ink-2">Enter the approved website above to begin private sign-in for a specific job.</p> : null}
          </div> : null}
          <button type="button" className="text-xs font-semibold text-brand-light flex gap-1 items-center"
            onClick={() => {onClose(); nav("/connections");}}>Manage all connections <ArrowRight size={14}/></button>
        </section>
      </div>
      <div className="p-3 border-t border-line flex items-center justify-between gap-2">
        <p className="text-[11px] text-ink-2 flex gap-1 items-center"><ShieldCheck size={14}/> Sign-ins belong to you.</p>
        <button type="button" disabled={loading} onClick={refresh}
          className="text-xs font-bold text-ink border border-line px-3 py-2 rounded-lg flex gap-1 items-center"><RefreshCw size={14}/> Refresh</button>
      </div>
    </div>
  </div>;
}
